import React, { VideoHTMLAttributes, useState, useRef, useEffect, CSSProperties } from 'react'
import ImageComponent from './ImageComponent'
import { getYTId } from './util/matchSocialNetworkUrl'
import { Box } from '@mui/material'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import { useCachedMedia } from './hooks/useCachedMedia'

interface IProps extends VideoHTMLAttributes<any> {
  withThumbnail?: boolean
  fallbackThumb: string | undefined
  type?: string
}

const VideoComponent: React.FC<IProps> = ({ fallbackThumb, type, src, ...props }) => {
  const [showVideo, setShowVideo] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  // Resolve video source through media cache for non-YouTube videos
  const isBlobSrc = typeof src === 'string' && src.startsWith('blob:')
  const shouldUseCache = type !== 'YT_VIDEO' && !isBlobSrc
  const cachedSrc = shouldUseCache ? useCachedMedia(src, undefined) : undefined
  const effectiveSrc = type === 'YT_VIDEO' ? undefined : (isBlobSrc ? src : (cachedSrc || undefined))

  // Static styles
  const imageStyle: CSSProperties = {
    height: '336px', // equivalent to theme.spacing(42)
    width: '100%',
    objectFit: 'cover',
    borderTopLeftRadius: '2%',
    borderTopRightRadius: '2%',
  }

  const thumbnailButtonStyle: CSSProperties = {
    width: props.width || '100%',
    height: props.height || 'auto',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'block',
    textDecoration: 'none',
    margin: 0,
    padding: 0,
    border: 0,
  }

  const thumbnailImageStyle: CSSProperties = {
    margin: 0,
    minHeight: '300px',
    width: props.width || '100%',
    objectFit: 'cover',
  }

  const playIconStyle: CSSProperties = {
    height: '60px',
    left: 'calc(50% - 30px)',
    position: 'absolute',
    top: 'calc(50% - 30px)',
    transition: 'all 0.3s ease-in-out',
    width: '60px',
    color: '#fff',
    filter: 'drop-shadow( 0px 0px 3px rgba(0, 0, 0, .15))'
  }

  // Update video source when src changes without remounting the element
  useEffect(() => {
    if (videoRef.current && effectiveSrc) {
      // Normalize URLs for comparison (remove trailing slashes, normalize protocol, etc.)
      const currentSrc = videoRef.current.src
      const newSrc = effectiveSrc.startsWith('http') ? effectiveSrc : new URL(effectiveSrc, window.location.origin).href
      
      // Only update if the source actually changed
      if (currentSrc !== newSrc) {
        const wasPlaying = !videoRef.current.paused
        const currentTime = videoRef.current.currentTime
        
        videoRef.current.src = newSrc
        // Don't call load() here - let the browser use its cache
        // videoRef.current.load() 
        
        // Restore playback state if it was playing
        if (wasPlaying && props.autoPlay) {
          videoRef.current.currentTime = currentTime
          videoRef.current.play().catch(() => {
            // Ignore play errors (e.g., if autoplay is blocked)
          })
        }
      }
    }
  }, [effectiveSrc, props.autoPlay])

  return (
    <>
      {props.withThumbnail && src && (
        <>
          {(!showVideo || !effectiveSrc) && (
            <button style={thumbnailButtonStyle} onClick={() => setShowVideo(true)}>
              <Box>
                <ImageComponent style={thumbnailImageStyle} src={fallbackThumb} loading="lazy" fallback=""/>
                <PlayCircleIcon style={playIconStyle} color="disabled" />
              </Box>
            </button>
          )}
          {showVideo && effectiveSrc && (
            <video
              {...props}
              ref={videoRef}
              autoPlay
              preload="auto"
              width={props.width || '300px'}
              height={props.height || 'auto'}
              src={effectiveSrc}/>
          )}
        </>
      )}
      {!props.withThumbnail && (
        <>
          {type === 'YT_VIDEO' && src && (
            <iframe
              width={props.width || '100%'}
              height={props.height || 400}
              style={{ border: 0 }}
              src={'https://www.youtube.com/embed/' + getYTId(src)}/>
          )}
          {!type && effectiveSrc && (
            <video
              {...props}
              ref={videoRef}
              preload="auto"
              width={props.width || '300px'}
              height={props.height || 'auto'}
              src={effectiveSrc}/>
          )}
          {!type && !effectiveSrc && fallbackThumb && (
            <ImageComponent style={imageStyle} fallback={fallbackThumb}/>
          )}
        </>
      )}
    </>
  )
}
export default VideoComponent
